import { useEffect, useState } from "react";
import { Bot, Plus, Loader2, Copy, Check, Code2, Sparkles, Trash2, ExternalLink, BookOpen, MessageSquare, Users, X, BarChart3, TrendingUp, HelpCircle, Zap } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { getBots, createBot, deleteBot, ingestBot, autoConfigBot, botInsights, getPlanUsage } from "@/lib/api";

const EMBED_ORIGIN = process.env.REACT_APP_BACKEND_URL;

function CopyBtn({ text, testid }) {
  const [done, setDone] = useState(false);
  return (
    <button
      data-testid={testid}
      onClick={() => {
        navigator.clipboard.writeText(text);
        setDone(true);
        toast.success("Copied to clipboard");
        setTimeout(() => setDone(false), 1500);
      }}
      className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand hover:gap-2 transition-all"
    >
      {done ? <Check size={14} /> : <Copy size={14} />} {done ? "Copied" : "Copy"}
    </button>
  );
}

export default function Chatbots() {
  const [bots, setBots] = useState(null);
  const [plan, setPlan] = useState(null);
  const [creating, setCreating] = useState(false);
  const [embedFor, setEmbedFor] = useState(null);
  const [analyticsFor, setAnalyticsFor] = useState(null);

  const load = () => {
    getBots().then((d) => setBots(d.bots || [])).catch(() => setBots([]));
    getPlanUsage().then(setPlan).catch(() => setPlan(null));
  };
  useEffect(() => { load(); }, []);

  if (bots === null) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-brand" size={26} /></div>;

  const atBotCap = plan && plan.bots_used >= plan.bots_cap;

  return (
    <div data-testid="portal-chatbots">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">AI Chatbots</h1>
          <p className="text-slate-500 mt-1.5 text-sm max-w-xl">Build a branded AI assistant for your website, train it on your content, then drop a one-line snippet on any site. Works for your own brands and external clients.</p>
        </div>
        <button onClick={() => atBotCap ? toast.info(`You've reached your plan's ${plan.bots_cap}-bot limit. Upgrade to add more.`) : setCreating(true)} data-testid="new-bot-btn" className="inline-flex items-center gap-2 bg-brand text-white font-semibold rounded-xl px-5 py-3 hover:-translate-y-0.5 transition-transform shrink-0 disabled:opacity-60">
          <Plus size={17} /> New chatbot
        </button>
      </div>

      {plan && <PlanStrip plan={plan} />}

      {bots.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-12 text-center mt-6" data-testid="bots-empty">
          <Bot className="mx-auto text-brand mb-3" size={30} />
          <p className="text-slate-700 font-semibold">No chatbots yet</p>
          <p className="text-slate-400 text-sm mt-1">Create your first AI assistant in under a minute.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6" data-testid="bots-grid">
          {bots.map((b) => (
            <BotCard key={b.id} bot={b} onEmbed={() => setEmbedFor(b)} onAnalytics={() => setAnalyticsFor(b)} onChanged={load} />
          ))}
        </div>
      )}

      {creating && <CreateBot onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
      {embedFor && <EmbedDialog bot={embedFor} onClose={() => setEmbedFor(null)} />}
      {analyticsFor && <AnalyticsDialog bot={analyticsFor} onClose={() => setAnalyticsFor(null)} />}
    </div>
  );
}

function UsageBar({ used, cap, label, unlimited }) {
  const pct = unlimited ? 8 : Math.min(100, Math.round((used / Math.max(cap, 1)) * 100));
  const danger = !unlimited && pct >= 90;
  return (
    <div>
      <div className="flex justify-between text-xs mb-1.5">
        <span className="text-slate-500">{label}</span>
        <span className="font-mono font-semibold text-slate-700">{used}{unlimited ? "" : ` / ${cap}`}{unlimited ? " · unlimited" : ""}</span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full transition-all ${danger ? "bg-rose-500" : "bg-brand"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function PlanStrip({ plan }) {
  const unlimited = plan.bots_cap >= 1000000;
  const msgPct = plan.messages_cap ? (plan.messages_used / plan.messages_cap) * 100 : 0;
  const nudge = plan.plan !== "business" && msgPct >= 80;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-5" data-testid="plan-strip">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Zap size={16} className="text-brand" />
          <span className="text-sm font-bold text-slate-900 capitalize">{plan.plan} plan</span>
        </div>
        {plan.plan === "free" && (
          <Link to="/ai-chatbot#pricing" data-testid="upgrade-link" className="text-xs font-semibold text-brand hover:underline inline-flex items-center gap-1">Upgrade <TrendingUp size={13} /></Link>
        )}
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <UsageBar used={plan.bots_used} cap={plan.bots_cap} label="Chatbots" unlimited={unlimited} />
        <UsageBar used={plan.messages_used} cap={plan.messages_cap} label="Messages this month" />
      </div>
      {nudge && (
        <div className="mt-4 flex items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3" data-testid="usage-nudge">
          <p className="text-sm text-amber-800">
            You've used <span className="font-bold">{Math.round(msgPct)}%</span> of this month's messages. Upgrade to keep your bots answering without interruption.
          </p>
          <Link to="/ai-chatbot#pricing" data-testid="nudge-upgrade-link" className="shrink-0 bg-amber-500 text-white text-xs font-bold rounded-lg px-4 py-2 hover:bg-amber-600 transition-colors inline-flex items-center gap-1">
            Upgrade <TrendingUp size={13} />
          </Link>
        </div>
      )}
    </div>
  );
}

function BotCard({ bot, onEmbed, onAnalytics, onChanged }) {
  const [ingestUrl, setIngestUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const doIngest = async () => {
    if (!ingestUrl.trim()) return;
    setBusy(true);
    try {
      const r = await ingestBot(bot.id, ingestUrl.trim());
      if (!r.chunks) {
        toast.warning("Couldn't read any content from that URL (it may be JavaScript-rendered or blocked). Try a different page.");
      } else {
        toast.success(`Trained on ${r.pages_ingested} page(s) · ${r.chunks} chunks`);
      }
      setIngestUrl("");
      onChanged();
    } catch { toast.error("Could not read that URL."); }
    setBusy(false);
  };

  const doDelete = async () => {
    if (!window.confirm(`Delete "${bot.name}"? This removes its knowledge and embed key.`)) return;
    try { await deleteBot(bot.id); toast.success("Chatbot deleted"); onChanged(); }
    catch { toast.error("Could not delete."); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-5 flex flex-col" data-testid="bot-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: bot.primary_color }}>
          <Bot size={20} className="text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-heading text-base font-bold text-slate-900 truncate">{bot.name}</h3>
          <p className="text-xs text-slate-400 truncate">{bot.site || bot.industry || "Website assistant"}</p>
        </div>
        <button onClick={doDelete} data-testid="bot-delete" className="text-slate-300 hover:text-rose-500 transition-colors"><Trash2 size={16} /></button>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-4">
        <Metric icon={BookOpen} label="Knowledge" value={bot.knowledge_count ?? 0} />
        <Metric icon={MessageSquare} label="Chats" value={bot.conversations ?? 0} />
        <Metric icon={Users} label="Leads" value={bot.leads ?? 0} />
      </div>

      <div className="mt-4 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 flex items-center gap-2">
        <code className="text-[11px] font-mono text-slate-500 truncate flex-1">{bot.public_key}</code>
        <CopyBtn text={bot.public_key} testid="bot-key-copy" />
      </div>

      <div className="mt-3 flex gap-2">
        <input value={ingestUrl} onChange={(e) => setIngestUrl(e.target.value)} placeholder="Train on a URL…" data-testid="bot-ingest-url"
          className="flex-1 min-w-0 text-sm bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-900 outline-none focus:border-brand" />
        <button onClick={doIngest} disabled={busy} data-testid="bot-ingest-btn" className="bg-slate-900 text-white text-sm font-semibold rounded-lg px-3 py-2 disabled:opacity-50">
          {busy ? <Loader2 size={15} className="animate-spin" /> : "Train"}
        </button>
      </div>

      <div className="mt-auto pt-4 flex items-center gap-4 border-t border-slate-100 mt-4">
        <button onClick={onAnalytics} data-testid="bot-analytics-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-brand transition-colors"><BarChart3 size={15} /> Analytics</button>
        <button onClick={onEmbed} data-testid="bot-embed-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:gap-2 transition-all"><Code2 size={15} /> Embed & API</button>
        <a href={`/assistant/${bot.public_key}`} target="_blank" rel="noopener noreferrer" data-testid="bot-test-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900"><ExternalLink size={15} /> Test</a>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="bg-slate-50 rounded-lg p-2.5 text-center">
      <Icon size={14} className="mx-auto text-slate-400" />
      <p className="font-heading text-lg font-bold text-slate-900 leading-tight mt-1">{value}</p>
      <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
    </div>
  );
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className={`bg-white rounded-2xl shadow-2xl w-full ${wide ? "max-w-2xl" : "max-w-lg"} max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white z-10">
          <h3 className="font-heading text-lg font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} data-testid="modal-close" className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function CreateBot({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", site: "", welcome_message: "Hi! How can I help you today?", primary_color: "#2563EB", suggestions: "", system_prompt: "" });
  const [saving, setSaving] = useState(false);
  const [autoing, setAutoing] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const autoConfig = async () => {
    if (!form.site.trim()) { toast.error("Enter your website URL first."); return; }
    setAutoing(true);
    try {
      const d = await autoConfigBot(form.site.trim());
      setForm((f) => ({
        ...f,
        name: f.name || d.name || "Website Assistant",
        welcome_message: d.welcome_message || f.welcome_message,
        suggestions: (d.suggestions || []).join(", "),
        system_prompt: d.system_prompt || "",
      }));
      toast.success("Auto-configured from your website");
    } catch { toast.error("Could not read that website."); }
    setAutoing(false);
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Give your chatbot a name."); return; }
    setSaving(true);
    try {
      await createBot({
        name: form.name.trim(),
        site: form.site.trim(),
        welcome_message: form.welcome_message.trim(),
        primary_color: form.primary_color,
        suggestions: form.suggestions.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 6),
        system_prompt: form.system_prompt.trim(),
      });
      toast.success("Chatbot created");
      onCreated();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not create the chatbot.";
      toast.error(msg);
    }
    setSaving(false);
  };

  const inputCls = "w-full text-sm bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-slate-900 outline-none focus:border-brand";

  return (
    <Modal title="New AI chatbot" onClose={onClose}>
      <div className="space-y-4" data-testid="create-bot-form">
        <div>
          <label className="text-xs font-semibold text-slate-600">Website URL (optional — we can auto-configure)</label>
          <div className="flex gap-2 mt-1.5">
            <input value={form.site} onChange={(e) => set("site", e.target.value)} placeholder="yourbrand.com" className={inputCls} data-testid="create-bot-site" />
            <button onClick={autoConfig} disabled={autoing} data-testid="create-bot-autoconfig" className="inline-flex items-center gap-1.5 bg-blue-50 text-brand font-semibold text-sm rounded-lg px-3 whitespace-nowrap disabled:opacity-50">
              {autoing ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Auto-fill
            </button>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600">Name</label>
          <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Acme Support Bot" className={`${inputCls} mt-1.5`} data-testid="create-bot-name" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600">Welcome message</label>
          <input value={form.welcome_message} onChange={(e) => set("welcome_message", e.target.value)} className={`${inputCls} mt-1.5`} data-testid="create-bot-welcome" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600">Suggested questions (comma-separated)</label>
          <input value={form.suggestions} onChange={(e) => set("suggestions", e.target.value)} placeholder="What do you offer?, How do I contact you?" className={`${inputCls} mt-1.5`} data-testid="create-bot-suggestions" />
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs font-semibold text-slate-600">Brand color</label>
          <input type="color" value={form.primary_color} onChange={(e) => set("primary_color", e.target.value)} className="w-10 h-9 rounded border border-slate-200 cursor-pointer" data-testid="create-bot-color" />
          <span className="text-xs font-mono text-slate-400">{form.primary_color}</span>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600">Instructions (optional)</label>
          <textarea value={form.system_prompt} onChange={(e) => set("system_prompt", e.target.value)} rows={3} placeholder="Describe how the assistant should behave and what it knows…" className={`${inputCls} mt-1.5 resize-none`} data-testid="create-bot-prompt" />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button onClick={onClose} className="text-sm font-semibold text-slate-500 px-4 py-2.5">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="create-bot-submit" className="inline-flex items-center gap-2 bg-brand text-white font-semibold rounded-xl px-5 py-2.5 disabled:opacity-50">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Create chatbot
          </button>
        </div>
      </div>
    </Modal>
  );
}

function EmbedDialog({ bot, onClose }) {
  const snippet = `<script src="${EMBED_ORIGIN}/api/widget.js" data-wtai-key="${bot.public_key}" defer></script>`;
  const apiExample = `curl -X POST "${EMBED_ORIGIN}/api/ai/public/${bot.public_key}/chat" \\
  -H "Content-Type: application/json" \\
  -d '{"session_id": "user-123", "message": "Hello!"}'`;

  return (
    <Modal title={`Embed "${bot.name}"`} onClose={onClose} wide>
      <div className="space-y-6" data-testid="embed-dialog">
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-slate-800">1. Website widget (recommended)</p>
            <CopyBtn text={snippet} testid="copy-embed-snippet" />
          </div>
          <p className="text-xs text-slate-500 mb-2">Paste this before the closing <code className="bg-slate-100 px-1 rounded">&lt;/body&gt;</code> tag on any page. A floating chat bubble appears instantly.</p>
          <pre className="bg-slate-900 text-emerald-200 text-xs rounded-xl p-4 overflow-x-auto whitespace-pre-wrap break-all" data-testid="embed-snippet"><code>{snippet}</code></pre>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-slate-800">2. REST API</p>
            <CopyBtn text={apiExample} testid="copy-api-example" />
          </div>
          <p className="text-xs text-slate-500 mb-2">Build your own UI by calling the chat endpoint directly. Reuse the same <code className="bg-slate-100 px-1 rounded">session_id</code> to keep conversation context.</p>
          <pre className="bg-slate-900 text-blue-200 text-xs rounded-xl p-4 overflow-x-auto whitespace-pre-wrap break-all" data-testid="api-example"><code>{apiExample}</code></pre>
        </div>

        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
          <p className="text-xs text-slate-600"><span className="font-semibold text-slate-800">Public key:</span> <code className="font-mono">{bot.public_key}</code></p>
          <p className="text-xs text-slate-500 mt-1.5">Config endpoint: <code className="font-mono">GET {EMBED_ORIGIN}/api/ai/public/{bot.public_key}</code></p>
        </div>
      </div>
    </Modal>
  );
}


function AnalyticsDialog({ bot, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { botInsights(bot.id).then(setData).catch(() => setData({ error: true })); }, [bot.id]);

  return (
    <Modal title={`${bot.name} · Analytics`} onClose={onClose} wide>
      {!data ? (
        <div className="flex justify-center py-14"><Loader2 className="animate-spin text-brand" size={24} /></div>
      ) : data.error ? (
        <p className="text-sm text-slate-500 py-8 text-center">Couldn't load analytics.</p>
      ) : (
        <div className="space-y-6" data-testid="analytics-dialog">
          <div className="grid grid-cols-3 gap-3">
            <Stat icon={MessageSquare} label="Conversations" value={data.conversations} />
            <Stat icon={BarChart3} label="Messages (all time)" value={data.messages} />
            <Stat icon={Users} label="Leads" value={data.leads_count} />
          </div>

          <div>
            <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2 mb-3"><Users size={15} className="text-brand" /> Captured leads</h4>
            {data.leads.length === 0 ? (
              <p className="text-sm text-slate-400">No leads captured yet. When a visitor shares an email or phone, it'll appear here (and we'll email you instantly).</p>
            ) : (
              <div className="space-y-2 max-h-52 overflow-y-auto" data-testid="analytics-leads">
                {data.leads.map((l, i) => (
                  <div key={i} className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm">
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                      {l.email && <span className="font-semibold text-slate-800">{l.email}</span>}
                      {l.phone && <span className="font-semibold text-slate-800">{l.phone}</span>}
                    </div>
                    {l.message && <p className="text-slate-500 text-xs mt-1 line-clamp-2">"{l.message}"</p>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2 mb-3"><HelpCircle size={15} className="text-brand" /> Top unanswered questions</h4>
            {(!data.top_questions || data.top_questions.length === 0) ? (
              <p className="text-sm text-slate-400">Nothing yet. Questions your bot couldn't answer show up here — great hints for what to add to its training.</p>
            ) : (
              <div className="space-y-2" data-testid="analytics-gaps">
                {data.top_questions.slice(0, 8).map((g, i) => (
                  <div key={i} className="flex items-center justify-between bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                    <span className="text-sm text-slate-700 truncate">{g.question}</span>
                    <span className="text-xs font-mono font-bold text-amber-700 shrink-0 ml-3">×{g.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 text-center">
      <Icon size={16} className="mx-auto text-brand" />
      <p className="font-heading text-2xl font-bold text-slate-900 mt-1.5">{value ?? 0}</p>
      <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
    </div>
  );
}
